import { useCallback } from 'react';
import { ApolloCache, ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  ContractMessageTemplatesQuery,
  ContractMessageVariablesQuery,
  useContractMessageTemplatesQuery,
  useContractMessageVariablesQuery,
  useDeleteContractMessageTemplateMutation,
  useMessageTemplatesEditableQuery,
  useSaveContractMessageTemplateMutation,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useSmsPermissions } from '../Sms/useSmsPermissions';
import { useToasts } from '../Toast/useToasts';
import {
  IMessageVariableInfo,
  IMessageVariableText,
  MessageChannel,
  messageTemplateUses,
  messageVariableText,
} from './messageModel';
import { IEsignFieldError, toEsignErrors } from './useEsignMutations';
import { useEsignPermissions } from './useEsignPermissions';

/**
 * ТЗ 0078 — справочник шаблонов SMS и писем по договору.
 *
 * Читают роли договоров студентов, правит тот, кому разрешил сервер
 * (`canEditMessageTemplates`). ТЗ 0084: тексты робота (шаги лесенок,
 * «Напомнить сейчас», письма-приглашения) — тоже шаблоны этого справочника;
 * занятый кодом шаблон (`usedIn`) не выключить и не удалить.
 *
 * ТЗ 0081: справочник стал общим для тем SMS студентам (`topic`: ручная
 * SMS, рассылка по группе, оплата фактуры, доступ в LMS). Без темы — как
 * раньше, договоры (`esign_signature`). Шаблоны не договорной темы читают
 * все роли ERP (`MANAGEMENT_ROLES`), договорные — только роли договоров.
 */

/**
 * Название шаблона на языке интерфейса. Название — данные, и хранится
 * двумя строками: русской (`name`) и польской (`namePl`, 2026-09-23). В
 * польском интерфейсе — польская, если задана; иначе — русская.
 */
export const messageTemplateName = (
  template: { name: string; namePl?: string | null },
  language: string | null | undefined,
): string =>
  ((language || '').startsWith('pl') && template.namePl?.trim()) ||
  template.name;

export const useMessageTemplateName = () => {
  const { i18n } = useTranslation();
  const { language } = i18n;
  return useCallback(
    (template: { name: string; namePl?: string | null }): string =>
      messageTemplateName(template, language),
    [language],
  );
};

/**
 * ТЗ 0084: где используется шаблон — подписи на языке интерфейса
 * (`usedIn` / `usedInPl`).
 */
export const useMessageTemplateUses = () => {
  const { i18n } = useTranslation();
  const { language } = i18n;
  return useCallback(
    (template: {
      usedIn?: readonly string[] | null;
      usedInPl?: readonly string[] | null;
    }): string[] => messageTemplateUses(template, language),
    [language],
  );
};

/**
 * ТЗ 0084: описание подстановки (что это · откуда · пример) на языке
 * интерфейса.
 */
export const useMessageVariableText = () => {
  const { i18n } = useTranslation();
  const { language } = i18n;
  return useCallback(
    (variable: IMessageVariableInfo): IMessageVariableText =>
      messageVariableText(variable, language),
    [language],
  );
};

/** Тема договоров — шаблоны по умолчанию (без `topic`). */
const ESIGN_TEMPLATE_TOPIC = 'esign_signature';

/** Может ли текущая роль читать шаблоны темы — то же правило, что у сервера. */
const useCanReadTopic = (topic?: string | null): boolean => {
  const { canReadStudent } = useEsignPermissions();
  const { canUse } = useSmsPermissions();
  return topic && topic !== ESIGN_TEMPLATE_TOPIC ? canUse : canReadStudent;
};

export type IMessageTemplate = ContractMessageTemplatesQuery['messageTemplates'][number];

export type IMessageVariable = ContractMessageVariablesQuery['messageTemplateVariables'][number];

const EMPTY_TEMPLATES: IMessageTemplate[] = [];
const EMPTY_VARIABLES: IMessageVariable[] = [];

interface IUseMessageTemplatesOptions {
  /**
   * Выключенные тоже — справочник того, кто правит тему
   * (`useCanEditMessageTemplates`); остальным бэкенд их не отдаёт.
   */
  includeInactive?: boolean;
  enabled?: boolean;
  /** ТЗ 0081: тема шаблонов; пусто — договоры. */
  topic?: string | null;
}

/** Шаблоны одного канала (или всех, если канал не задан). */
export const useMessageTemplates = (
  channel: MessageChannel | null,
  {
    includeInactive = false,
    enabled = true,
    topic = null,
  }: IUseMessageTemplatesOptions = {},
): {
  templates: IMessageTemplate[];
  loading: boolean;
  error?: ApolloError;
} => {
  const canRead = useCanReadTopic(topic);
  const { data, loading, error } = useContractMessageTemplatesQuery({
    variables: {
      channel: channel || null,
      includeInactive,
      ...(topic ? { topic } : {}),
    },
    skip: !enabled || !canRead,
    fetchPolicy: 'cache-and-network',
  });
  return {
    templates: data?.messageTemplates ?? EMPTY_TEMPLATES,
    loading,
    error,
  };
};

export interface IMessageTemplateEditAccess {
  /** Сервер разрешил создавать, править и удалять шаблоны темы. */
  canEdit: boolean;
  /**
   * Ответ уже пришёл (или спрашивать некому). Пока `false`, экран не пишет
   * «вы видите на чтение»: у того, кто правит, это мигало бы при каждом
   * заходе.
   */
  known: boolean;
}

/**
 * Перепроверка 0084: может ли сотрудник создавать, править и удалять шаблоны
 * темы. Правило ОДНО и живёт на сервере (`canEditMessageTemplates`): Admin и
 * продавцы — все темы, финансы — ещё и тексты оплаты (`invoice_payment`).
 * Экран не повторяет его своей проверкой ролей (две копии правила однажды
 * разошлись бы): пока ответа нет — «нельзя», кнопок нет.
 */
export const useMessageTemplateEditAccess = (
  topic: string | null = null,
  enabled = true,
): IMessageTemplateEditAccess => {
  const canRead = useCanReadTopic(topic);
  const skip = !enabled || !canRead;
  const { data, error } = useMessageTemplatesEditableQuery({
    variables: topic ? { topic } : {},
    skip,
    fetchPolicy: 'cache-first',
  });
  return {
    canEdit: Boolean(data?.canEditMessageTemplates),
    known: skip || Boolean(data) || Boolean(error),
  };
};

export const useCanEditMessageTemplates = (
  topic: string | null = null,
  enabled = true,
): boolean => useMessageTemplateEditAccess(topic, enabled).canEdit;

export interface IMessageVariables {
  variables: IMessageVariable[];
  /** Справочник ещё грузится (и пока пуст). */
  loading: boolean;
  /** Не пришёл: сеть или отказ — справочник пуст, есть «Повторить». */
  failed: boolean;
  reload: () => void;
}

/**
 * Подстановки для справочника под полем — меняются только с выкладкой
 * бэкенда. Перепроверка 0084: отдаёт и «грузится», и «не пришёл» с
 * повтором — пустой справочник без объяснения выглядел как «подстановок нет».
 */
export const useMessageVariables = (
  enabled = true,
  topic: string | null = null,
): IMessageVariables => {
  const canRead = useCanReadTopic(topic);
  const { data, loading, error, refetch } = useContractMessageVariablesQuery({
    variables: topic ? { topic } : {},
    skip: !enabled || !canRead,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);
  const variables = data?.messageTemplateVariables ?? EMPTY_VARIABLES;
  return {
    variables,
    loading: loading && variables.length === 0,
    failed: Boolean(error) && variables.length === 0,
    reload,
  };
};

/**
 * Любая правка справочника меняет состав списков (создан, выключен,
 * удалён) — выбрасываем поле целиком, каждый открытый список перечитает
 * себя сам. Не `refetchQueries` по имени: так бьётся и по пропущенному
 * запросу (грабли `refetch-by-name-skipped-query`).
 */
const evictTemplateLists = (cache: ApolloCache<unknown>) => {
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'messageTemplates' });
  cache.gc();
};

export interface IMessageTemplateInput {
  id?: string | null;
  channel: MessageChannel;
  name: string;
  /**
   * Название для польского интерфейса. `undefined` — не менять: переключатель
   * «включён» шлёт шаблон без него и не должен стирать польское название.
   */
  namePl?: string;
  subject: string;
  body: string;
  isActive: boolean;
  /** ТЗ 0081: тема шаблона; пусто — договоры. */
  topic?: string | null;
}

export interface IMessageTemplateResult {
  ok: boolean;
  errors: IEsignFieldError[];
}

/**
 * Создать, изменить, выключить и удалить шаблон — кто вправе править тему
 * (`useCanEditMessageTemplates`; отказ сервера — `FORBIDDEN`).
 *
 * Ошибки полей (`REQUIRED`, `UNKNOWN_PLACEHOLDER`, `TOO_LONG`,
 * `NO_LINK_HERE`, `TEMPLATE_IN_USE`) рисует окно правки, поэтому `save`
 * тоста об отказе не поднимает; переключение «включён» и удаление со строки
 * таблицы (ТЗ 0084) тоже тихие — отказ пишется в самой строке.
 */
export const useMessageTemplateActions = () => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [
    saveTemplate,
    { loading: saving },
  ] = useSaveContractMessageTemplateMutation({ update: evictTemplateLists });
  const [
    deleteTemplate,
    { loading: deleting },
  ] = useDeleteContractMessageTemplateMutation({ update: evictTemplateLists });

  const notify = useCallback(
    (type: ToastTypes, title: string, text?: string) => {
      addToast({
        data: { type, title, text },
        type: GlobalToastTypeNames.Esign,
      });
    },
    [addToast],
  );

  const errorText = useCallback(
    (errors: IEsignFieldError[]): string =>
      errors
        .map((error) =>
          error.code
            ? t(`esign.templates.errors.${error.code}`, {
                defaultValue: error.message || error.code,
              })
            : error.message,
        )
        .filter(Boolean)
        .join('\n'),
    [t],
  );

  const crash = useCallback(
    (caught: unknown): IMessageTemplateResult => {
      const noAccess =
        caught instanceof ApolloError && caught.graphQLErrors.length > 0;
      notify(
        ToastTypes.error,
        t('esign.templates.toast.failed'),
        t(noAccess ? 'esign.sms.errors.NO_ACCESS' : 'esign.sms.errors.NETWORK'),
      );
      return { ok: false, errors: [] };
    },
    [notify, t],
  );

  const save = useCallback(
    async (
      input: IMessageTemplateInput,
      { silent = true }: { silent?: boolean } = {},
    ): Promise<IMessageTemplateResult> => {
      try {
        const { data } = await saveTemplate({
          variables: {
            id: input.id || null,
            channel: input.channel,
            name: input.name.trim(),
            namePl: input.namePl === undefined ? null : input.namePl.trim(),
            subject: input.channel === 'email' ? input.subject.trim() : '',
            body: input.body.trim(),
            isActive: input.isActive,
            topic: input.topic || null,
          },
        });
        const payload = data?.saveMessageTemplate;
        const errors = toEsignErrors(payload?.customErrors);
        if (payload?.success) {
          notify(ToastTypes.success, t('esign.templates.toast.saved'));
          return { ok: true, errors };
        }
        if (!silent) {
          notify(
            ToastTypes.error,
            t('esign.templates.toast.failed'),
            errorText(errors),
          );
        }
        return { ok: false, errors };
      } catch (caught) {
        return crash(caught);
      }
    },
    [crash, errorText, notify, saveTemplate, t],
  );

  const remove = useCallback(
    async (
      id: string,
      { silent = false }: { silent?: boolean } = {},
    ): Promise<IMessageTemplateResult> => {
      try {
        const { data } = await deleteTemplate({ variables: { id } });
        const payload = data?.deleteMessageTemplate;
        const errors = toEsignErrors(payload?.customErrors);
        if (payload?.success) {
          notify(ToastTypes.success, t('esign.templates.toast.deleted'));
          return { ok: true, errors };
        }
        // ТЗ 0084: строка таблицы показывает отказ у себя (`silent`) —
        // «шаблон используется» читается рядом с тем, что нажали.
        if (!silent) {
          notify(
            ToastTypes.error,
            t('esign.templates.toast.failed'),
            errorText(errors),
          );
        }
        return { ok: false, errors };
      } catch (caught) {
        return crash(caught);
      }
    },
    [crash, deleteTemplate, errorText, notify, t],
  );

  return { save, remove, errorText, busy: { saving, deleting } };
};
